import { type FC, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import type { JobsJobElasticSchemaWithId } from '@iconik-io/iconik-api/src/services/jobs';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { selectFailedTranscodeJobsByObjectId } from '@iconik-io/shared-lib/src/redux/selectors/jobs/selectors';
import { Icon } from '@iconik-io/uad';

import { FAILED, FAILED_TO_ARCHIVE } from '../../../constants/archive';
import { useAppSelector } from '../../../store/react_redux_typed';
import TooltipTrigger from '../../tooltip_trigger/tooltip_trigger';

import styles from '../asset_status_icon_detailed.module.css';

type Props = {
  object: SearchItemEntity;
};

const FailureStatuses: FC<Props> = ({ object }) => {
  const failedTranscodeJobs = useAppSelector((state) =>
    selectFailedTranscodeJobsByObjectId(state, object.id),
  );
  const [failedJobs, setFailedJobs] = useState<JobsJobElasticSchemaWithId[]>(
    [],
  );
  const initialFailedIds = useRef<Set<string>>(
    new Set(failedTranscodeJobs.map((job) => job.id)),
  );
  // Track only failed jobs that are registered after the component is mounted
  useEffect(() => {
    setFailedJobs(
      failedTranscodeJobs.filter(
        (job) => !initialFailedIds.current.has(job.id),
      ),
    );
  }, [failedTranscodeJobs]);

  const statuses = [];

  if (object.archive_status === FAILED_TO_ARCHIVE) {
    statuses.push(
      <TooltipTrigger
        tooltipText="Failed to archive"
        key="failed-archiving-status"
        placement="bottom"
        componentProps={{
          role: 'status',
          'aria-label': 'Failed to archive',
        }}
      >
        <div className={styles.status_icon}>
          <Icon name="archive_archived" />
        </div>
      </TooltipTrigger>,
    );
  }

  if (object.face_recognition_status === FAILED) {
    statuses.push(
      <TooltipTrigger
        tooltipText="Failed to run face recognition"
        key="face-recognition-fail-status"
        placement="bottom"
        componentProps={{
          role: 'status',
          'aria-label': 'Failed to run face recognition',
        }}
      >
        <div className={styles.status_icon}>
          <Icon name="face_recognition" />
        </div>
      </TooltipTrigger>,
    );
  }

  if (object.analyze_status === FAILED) {
    statuses.push(
      <TooltipTrigger
        tooltipText="Failed to analyze"
        key="analyze-fail-status"
        placement="bottom"
        componentProps={{
          role: 'status',
          'aria-label': 'Failed to analyze',
        }}
      >
        <div className={styles.status_icon}>
          <Icon name="analyze" />
        </div>
      </TooltipTrigger>,
    );
  }

  if (failedJobs.length > 0) {
    statuses.push(
      <TooltipTrigger
        tooltipText="Failed to transcode asset"
        key="transcode-fail-status"
        placement="bottom"
        componentProps={{
          role: 'status',
          'aria-label': 'Failed to transcode',
        }}
      >
        <div className={styles.status_icon}>
          <Icon name="transcoder" />
        </div>
      </TooltipTrigger>,
    );
  }

  if (statuses.length > 0) {
    return (
      <div className={classNames(styles.status_fail, styles.status)}>
        {statuses}
      </div>
    );
  }
  return null;
};

export default FailureStatuses;
